/**
 * Menu principal, avant d'entrer dans une partie : fond de la vallée, barre du haut
 * (navigation, joueurs en ligne, profil), écran courant (accueil, serveurs, amis), panneau
 * Amis à droite, fenêtres et notifications.
 */
import { useEffect } from 'preact/hooks';
import { Compass, House, LogOut, ShieldCheck, Users, WifiOff } from 'lucide-preact';
import { ToastView } from '../hud/components';
import { ui } from '../hud/ui-state';
import { lobby } from './lobby';
import { menu, menuActions, type MenuView } from './state';
import { HomeView } from './home';
import { ServersView } from './servers';
import { FriendsView } from './friends';
import { SocialSidebar } from './social';
import { MenuDialogs } from './dialogs';
import { Avatar } from './ui';

function MenuToasts() {
  const list = ui.toasts.value;
  return (
    <div class="mn-toasts" aria-live="polite">
      {list.map((t) => (
        <ToastView key={t.id} level={t.level} title={t.title} message={t.message} />
      ))}
    </div>
  );
}

const NAV: { view: MenuView; label: string; icon: typeof House }[] = [
  { view: 'home', label: 'Accueil', icon: House },
  { view: 'servers', label: 'Serveurs', icon: Compass },
  { view: 'friends', label: 'Amis', icon: Users },
];

function TopBar() {
  const me = lobby.me.value;
  const view = menu.view.value;
  const stats = lobby.stats.value;
  const attention = lobby.attention.value;
  const invites = (lobby.servers.value ?? []).filter((s) => s.invitedBy).length;
  return (
    <header class="mn-top">
      <div class="mn-brand">
        <svg class="mn-brand-mark" viewBox="0 0 32 32" aria-hidden="true">
          <path d="M2 27 L12 11 L17 18 L21 13 L30 27 Z" fill="currentColor" opacity="0.9" />
          <path d="M12 11 L15 15.5 L13.4 15 L12 16.6 L10.4 14.8 Z M21 13 L23.6 17 L22 16.6 L21 17.8 L19.8 16.4 Z" fill="var(--snow-0)" />
        </svg>
        <span class="mn-brand-name">SnowChain</span>
      </div>
      <nav class="mn-nav" aria-label="Menu principal">
        {NAV.map((n) => (
          <button key={n.view} type="button" class={view === n.view ? 'on' : ''} aria-current={view === n.view ? 'page' : undefined} onClick={() => (menu.view.value = n.view)}>
            <n.icon size={16} />
            {n.label}
            {n.view === 'friends' && attention > 0 && <span class="mn-badge">{attention}</span>}
            {n.view === 'servers' && invites > 0 && <span class="mn-badge mn-badge-soft">{invites}</span>}
          </button>
        ))}
      </nav>
      <div class="mn-top-end">
        <span class={`mn-status mn-status-${lobby.status.value}`} title={lobby.status.value === 'online' ? `Latence ${Math.round(lobby.rtt.value)} ms` : 'Reconnexion au serveur...'}>
          {lobby.status.value === 'online' ? (
            <>
              <span class="mn-live-dot" />
              {stats.playersOnline} joueur{stats.playersOnline > 1 ? 's' : ''} en ligne
            </>
          ) : (
            <>
              <WifiOff size={13} /> Reconnexion...
            </>
          )}
        </span>
        {me && (
          <button type="button" class="mn-me" onClick={() => (menu.dialog.value = { k: 'profile' })} title="Profil et compte">
            <Avatar id={me.id} name={me.name} size={30} presence="menu" />
            <span class="mn-me-text">
              <span class="mn-me-name">{me.name}</span>
              <span class="mn-me-kind">
                {me.kind === 'guest' ? (
                  'Compte invité'
                ) : (
                  <>
                    <ShieldCheck size={11} /> Compte protégé
                  </>
                )}
              </span>
            </span>
          </button>
        )}
        <button type="button" class="mn-ibtn" title="Se déconnecter" aria-label="Se déconnecter" onClick={() => menuActions.logout()}>
          <LogOut size={16} />
        </button>
      </div>
    </header>
  );
}

export function MainMenu() {
  const view = menu.view.value;
  useEffect(() => {
    // Raccourcis du menu : 1, 2, 3 pour les écrans (hors saisie).
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA') || e.ctrlKey || e.metaKey || e.altKey || menu.dialog.value) return;
      const v = ({ Digit1: 'home', Digit2: 'servers', Digit3: 'friends' } as const)[e.code as 'Digit1'];
      if (v) menu.view.value = v;
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <div class="mn-root">
      <div class="mn-backdrop" aria-hidden="true" />
      <div class="mn-shade" aria-hidden="true" />
      <div class="mn-snow" aria-hidden="true" />
      <TopBar />
      <div class={`mn-body mn-body-${view}`}>
        <main class="mn-main">
          {view === 'home' && <HomeView />}
          {view === 'servers' && <ServersView />}
          {view === 'friends' && <FriendsView />}
        </main>
        <SocialSidebar />
      </div>
      <MenuDialogs />
      <MenuToasts />
    </div>
  );
}
